/* src/pages/Donation/StepPages/Confirm/index.jsx */

import React, { useState } from "react";
import "./index.scss";
import { useSelector } from "react-redux";
import CustomLayout from "../../../../components/CustomLayout";
import CustomButton from "../../../../components/CustomButton";
import { useSearchParams } from "react-router-dom";
import {
  scheduleDonations,
  createPaymentIntent
} from "../../../../api/payments";
import {
  parseBreakdown,
  organizeDonations,
  parseSchedule
} from "../../../../utils/helpers";
import { getIpAddress } from "../../../../api/verification";
import { motion } from "framer-motion";
import { detailsFormVariants } from "../../../../assets/framer/styles";
import { useStripe } from "@stripe/react-stripe-js";
import ReactPixel from "react-facebook-pixel";

// 1) Import i18n
import { useTranslation } from "react-i18next";
import { ACTIVE_FUNDS } from "../../../../utils/constants";
import {
  getCanonicalTemplateKey,
  isFridayTemplateKey
} from "../../../../utils/templateUtils";

function toTwoDecimals(value, { truncate = false } = {}) {
  const num = typeof value === "string" ? parseFloat(value) : value;
  return truncate
    ? Math.floor(num * 100) / 100 // 129.999 → 129.99
    : Math.round(num * 100) / 100; // 129.999 → 130
}

const Confirm = () => {
  // 2) Destructure t
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  const stripe = useStripe();

  const timezone = useSelector((state) => state.donation.timezone);

  const tipAmountStr = searchParams.get("tipAmount") || "0";
  const tipAmount = parseFloat(tipAmountStr);

  const ref = searchParams.get("ref");

  const breakdownParam = searchParams.get("breakdown");
  const breakdown = breakdownParam
    ? JSON.parse(decodeURIComponent(breakdownParam))
    : [];

  const processingFeeParam = searchParams.get("processingFee");
  const processingFee = processingFeeParam ? parseFloat(processingFeeParam) : 0;

  const formValuesParam = searchParams.get("formValues");
  const formValues = formValuesParam
    ? JSON.parse(decodeURIComponent(formValuesParam))
    : {};

  const amountSum = breakdown
    .reduce((sum, item) => sum + (item.amount || 0), 0)
    .toFixed(2);

  const grandTotal = (
    parseFloat(amountSum) +
    processingFee +
    tipAmount
  ).toFixed(2);

  const breakdownParsed = parseBreakdown(breakdownParam);
  const scheduleParsed = parseSchedule(searchParams.get("schedule"));
  const donationScheduleBreakdown = organizeDonations(
    breakdownParsed,
    scheduleParsed
  );

  const template = getCanonicalTemplateKey(searchParams.get("template") || "");
  const appealCode = searchParams.get("appealCode") || searchParams.get("appeal");
  const isFridayTemplate = isFridayTemplateKey(template);
  const scheduleOption = Number(searchParams.get("scheduleOption") || 0);
  const handleNext = async () => {
    setLoading(true);
    const params = new URLSearchParams(searchParams);
    const paymentMethodId = params.get("paymentMethodId");
    const selectedOption = params.get("selectedOption");
    const customerId = params.get("customerId");
    const ipAddress = await getIpAddress();
    const userAgent = navigator.userAgent;

    let clientSecret;
    let tipStatus;

    // --- STEP 1: Process the Tip Payment (if tipAmount > 0) ---
    if (tipAmount > 0) {
      try {
        const tipPayload = {
          amount: 0, // so that only tip is charged
          tipAmount,
          currency: "cad",
          customerId,
          paymentMethodType: selectedOption
        };
        if (ref) {
          tipPayload.ref = ref;
        }
        if (appealCode) {
          tipPayload.appealCode = appealCode;
        }
        const tipResponse = await createPaymentIntent(tipPayload);
        if (tipResponse && tipResponse.clientSecret) {
          clientSecret = tipResponse.clientSecret;
          tipStatus = "success";
        }
      } catch (error) {
        console.error("Error processing tip payment:", error.message);
        alert(t("confirmTipError"));
        setLoading(false);
        return;
      }
    }

    // --- STEP 2: Schedule the Recurring Donations ---
    // (We pass tipAmount as 0 so it isn't charged again)
    const payload = {
      donationScheduleBreakdown: donationScheduleBreakdown.map((day) => ({
        date: day.date,
        allocations: day.allocations.map((alloc) => {
          const fund = ACTIVE_FUNDS.find(
            (c) => c.campaignId === alloc.campaignId
          );

          return {
            fundId: fund?.fundId,
            campaignId: fund?.campaignId,
            amount: alloc.amount
          };
        })
      })),
      paymentMethodId,
      donorDetails: formValues,
      paymentMethodType: selectedOption,
      ipAddress,
      userAgent,
      template,
      tipAmount: 0,
      timezone,
      ref,
      scheduleOption,
      ...(appealCode ? { appealCode } : {})
    };

    try {
      let response;
      response = await scheduleDonations(payload);

      // If the tip payment was successful, confirm it now
      if (tipStatus === "success") {
        if (!clientSecret) {
          console.log("Tip error: client secret not found");
        } else if (!paymentMethodId) {
          console.log("Tip error: payment method id not found");
        } else {
          try {
            await stripe.confirmCardPayment(clientSecret, {
              payment_method: paymentMethodId
            });
          } catch (err) {
            console.log("Error processing tip:", err);
          }
        }
      }

      try {
        ReactPixel.track("Purchase", {
          value: toTwoDecimals(amountSum, { truncate: true }), // total order value (number, not string)
          currency: "CAD" // 3‑letter ISO code
        });
      } catch (error) {
        console.log("Pixel purchase error: ", error);
      }

      params.set("stage", "5");
      if (response.scheduleId) params.set("scheduleId", response.scheduleId);
      if (response.pendingSchedule?._id) {
        params.set("pendingScheduleId", response.pendingSchedule._id);
        alert(t("confirmPendingCreated") || "Pending Ramadan schedule created. We will convert it when the start date is set.");
      }
      setSearchParams(params);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error scheduling donation:", error.message);
      alert(t("confirmDonationError"));
    }
  };

  return (
    <CustomLayout
      // 3) Use t(...) for title and description
      title={t("confirmTitle")}
      description={t("confirmDescription")}
      button={
        <CustomButton
          text={
            isFridayTemplate
              ? t("confirmFridayGiverButton")
              : t("confirmDonateButton")
          }
          onClick={handleNext}
          loading={loading}
        />
      }
    >
      <motion.div
        className="donation-breakdown"
        variants={detailsFormVariants}
        initial="hidden"
        animate="visible"
      >
        <p className="donation-breakdown-title">
          {isFridayTemplate
            ? t("confirmDonationAmountFridayTitle")
            : t("confirmDonationAmountTitle")}
        </p>
        <p className="donation-breakdown-sum">${amountSum}</p>

        {ref && ref.toLocaleLowerCase().includes("ogn") && (
          <>
            <hr className="custom-line" />
            <div className="donation-breakdown-line">
              <p className="donation-breakdown-line-name">
                {t("paymentDetailsBusinessName")}
              </p>
              <p className="donation-breakdown-line-amount">
                {formValues?.businessName}
              </p>
            </div>
          </>
        )}

        {isFridayTemplate && (
          <>
            <hr className="custom-line" />
            <div className="donation-breakdown-line">
              <p className="donation-breakdown-line-name">
                {t("confirmFrequencyLabel")}
              </p>
              <p className="donation-breakdown-line-amount">
                {t("confirmEveryFridayLabel")}
              </p>
            </div>
          </>
        )}

        <hr className="custom-line" />

        {breakdown.map((item, index) => (
          <div className="donation-breakdown-line" key={index}>
            <p className="donation-breakdown-line-name">
              {index + 1}.{" "}
              {ACTIVE_FUNDS.find((f) => f.fundId === item.fundId)?.name}
            </p>
            <p className="donation-breakdown-line-amount">
              ${item.amount.toFixed(2)}
            </p>
          </div>
        ))}

        {tipAmount > 0 && (
          <>
            <hr className="custom-line" />
            <div className="donation-breakdown-line">
              <p className="donation-breakdown-line-name">
                {ref && ref === "tcq"
                  ? t("confirmQuranBoxLabel")
                  : t("confirmTipLabel")}
              </p>
              <p className="donation-breakdown-line-amount">
                ${tipAmount.toFixed(2)}
              </p>
            </div>
          </>
        )}

        <hr className="custom-line" />
        <div className="donation-breakdown-line">
          <p className="donation-breakdown-line-name">{t("confirmSubTotal")}</p>
          <p className="donation-breakdown-line-amount">${amountSum}</p>
        </div>

        {processingFee > 0 && (
          <div className="donation-breakdown-line">
            <p className="donation-breakdown-line-name">
              {t("confirmProcessingFee")}
            </p>
            <p className="donation-breakdown-line-amount">${processingFee}</p>
          </div>
        )}

        <hr className="custom-line" />
        <div className="donation-breakdown-line">
          <p className="donation-breakdown-line-name">{t("confirmTotal")}</p>
          <p className="donation-breakdown-line-amount">${grandTotal}</p>
        </div>
      </motion.div>
    </CustomLayout>
  );
};

export default Confirm;
